import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Paper,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Loyalty as LoyaltyIcon,
  Percent as PercentIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@mui/icons-material";
import { getLoyaltySettings, updateLoyaltySettings } from "../api/posApi";

const LoyaltySettings = () => {
  const [loyaltyPercentage, setLoyaltyPercentage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await getLoyaltySettings();
        setLoyaltyPercentage(data.percentage);
      } catch (error) {
        console.error("Error fetching loyalty settings:", error);
        setError("Failed to load loyalty settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess(false);
      await updateLoyaltySettings(loyaltyPercentage);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating loyalty settings:", error);
      setError("Failed to update loyalty settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 600,
          mx: "auto",
          p: 4,
          borderRadius: 4,
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[3]
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <LoyaltyIcon
              color="primary"
              sx={{ fontSize: 40 }}
            />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Loyalty Program Settings
            </Typography>
          </Stack>

          <Typography variant="body1" color="text.secondary">
            Configure the reward percentage for customer loyalty program
          </Typography>

          <Divider sx={{ my: 2 }} />

          {error && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              onClose={() => setError("")}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              onClose={() => setSuccess(false)}
              sx={{ mb: 2 }}
            >
              Loyalty percentage updated successfully!
            </Alert>
          )}

          <Card
            variant="outlined"
            sx={{
              borderColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                boxShadow: theme.shadows[2]
              }
            }}
          >
            <CardContent>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PercentIcon color="primary" />
                  <Typography variant="h6" fontWeight="600">
                    Reward Percentage
                  </Typography>
                </Stack>

                <TextField
                  label="Loyalty Percentage"
                  type="number"
                  fullWidth
                  value={loyaltyPercentage}
                  onChange={(e) => setLoyaltyPercentage(Number(e.target.value))}
                  InputProps={{
                    endAdornment: (
                      <Typography color="text.secondary">%</Typography>
                    ),
                    inputProps: {
                      min: 0,
                      max: 100,
                      step: 0.1
                    }
                  }}
                  helperText="Enter the percentage customers will earn as rewards"
                  variant="outlined"
                  size="medium"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper
                    }
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      minWidth: 150,
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: theme.shadows[2]
                      }
                    }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Changes will take effect immediately for all new transactions
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoyaltySettings;